import { useAuth0 } from "@auth0/auth0-react"
import React, { useCallback, useEffect, useState } from "react"
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap"
import { ApiClient } from "../../ApiClient"
import { MessageType, useMessageReport } from "../../components/MessageReporter"
import { useParams } from "react-router-dom"
import { FirmwareBoardversions } from "../../components/Firmware/FirmwareBoardversions"
import { FirmwareVersionInput } from "../../components/Firmware/FirmwareVersionInput"
import { useApi } from "../../hooks/ApiProvider"
import { CloudError, Permission } from "../../api"
import { RoleProtect } from "../../components/Authorization/RoleProtect"

export const FirmwareDetail = () => {
    const { getAccessTokenSilently } = useAuth0()
    const { addMessage } = useMessageReport()
    const { id } = useParams()
    const { managementApi } = useApi()

    const [uri, setUri] = useState("...")
    const [deviceType, setDeviceType] = useState("...")
    const [version, setVersion] = useState("...")
    const [boardType, setBoardType] = useState()
    const [fwType, setFwType] = useState()
    const [entries, setEntries] = useState([])

    const [editMode, setEditMode] = useState(false)
    const [newVersion, setNewVersion] = useState("")

    useEffect(() => {
        getAccessTokenSilently()
            .then((token) =>
                ApiClient.get("/v2/management/firmwares/" + id, {
                    Authorization: "Bearer " + token,
                })
            )
            .then(({ data }) => {
                setVersion(data.version)
                setDeviceType(data.device_type)
                setUri(data.uri)
                setBoardType(data.board_type)
                setFwType(data.type)
                setEntries(data.children)
            })
            .catch((e) => {
                addMessage(
                    "Failed to load firmware",
                    "Something went wrong while loading the firmware, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
    }, [getAccessTokenSilently, id, addMessage])

    const updateSet = useCallback(() => {
        if (newVersion === version) {
            setEditMode(false)
            return
        }

        managementApi
            .changeFirmware({
                firmwareId: id,
                firmware: {
                    type: "SET",
                    version: newVersion,
                    deviceType: deviceType,
                    children: entries.map((entry) => entry.firmware_id),
                },
            })
            .then(() => {
                addMessage(
                    "Successfully updated firmware",
                    "The firmware set's version was successfully changed.",
                    MessageType.success
                )
                setVersion(newVersion)
                setEditMode(false)
            })
            .catch((e) => {
                if (
                    e instanceof CloudError &&
                    e.diagnostics.diagnostics?.code === "duplicate_version"
                ) {
                    addMessage(
                        "Failed to store firmware",
                        "A firmware with this version already exists.",
                        MessageType.error,
                        e
                    )
                } else {
                    addMessage(
                        "Failed to store firmware",
                        "Something went wrong while storing the firmware, " +
                            "see the console for more details.",
                        MessageType.error,
                        e
                    )
                }
            })
    }, [managementApi, addMessage, id, version, newVersion, deviceType, entries])

    return (
        <Row className="content-section">
            <Col xs={12}>
                {fwType === "BOARDVERSION" && <h2>Board version details</h2>}
                {fwType === "VERSION" && <h2>Firmware version details</h2>}
                {fwType === "SET" && <h2>Firmware set details</h2>}
                <Form.Group className="mb-3">
                    <Form.Label>Firmware ID</Form.Label>
                    <Form.Control readOnly disabled type="text" value={id} />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Version</Form.Label>
                    {fwType === "SET" ? (
                        editMode ? (
                            <FirmwareVersionInput
                                initialVersion={version}
                                setVersion={setNewVersion}
                            >
                                <Button
                                    variant="secondary"
                                    onClick={updateSet}
                                    disabled={!newVersion}
                                >
                                    Save
                                </Button>
                            </FirmwareVersionInput>
                        ) : (
                            <InputGroup className="mb-3">
                                <Form.Control readOnly disabled type="text" value={version} />
                                <RoleProtect
                                    allowedPermissions={[Permission.SetAnyResourceFirmware]}
                                >
                                    <Button variant="secondary" onClick={() => setEditMode(true)}>
                                        Edit
                                    </Button>
                                </RoleProtect>
                            </InputGroup>
                        )
                    ) : (
                        <Form.Control readOnly disabled type="text" value={version} />
                    )}
                </Form.Group>
            </Col>
            <Col xs={12} md={3}>
                <Form.Group className="mb-3">
                    <Form.Label>Device type</Form.Label>
                    <Form.Control disabled value={deviceType} />
                </Form.Group>
            </Col>

            {fwType === "BOARDVERSION" && (
                <>
                    <Col xs={12} md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label>Board type</Form.Label>
                            <Form.Control disabled value={boardType} />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={9}>
                        <Form.Group className="mb-3">
                            <Form.Label>Uri</Form.Label>
                            <Form.Control disabled value={uri} />
                        </Form.Group>
                    </Col>
                </>
            )}
            {fwType === "VERSION" && (
                <Col xs={12} md={9}>
                    <Form.Group className="mb-3">
                        <Form.Label>Uri</Form.Label>
                        <Form.Control disabled value={uri} />
                    </Form.Group>
                </Col>
            )}
            {fwType === "SET" && <FirmwareBoardversions entries={entries} />}
        </Row>
    )
}
